import {
  Divider, IconButton, IconButtonType, NoData, Spinner
} from "@whitespace/whitespace-ui";
import { useState, useCallback, useEffect } from "react";
import {
  Card, Col, ListGroup, Row, ToggleButton, ToggleButtonGroup
} from "react-bootstrap";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import { mapUserResponseItemToUser } from "mappers/mapUserResponseItemToUser/mapUserResponseItemToUser";
import { IUser } from "interfaces/models/IUser";
import { UserType } from "interfaces/models/UserType";
import { UserQuickFilter } from "interfaces/models/UserQuickFilter";
import { UserStatusType } from "interfaces/models/UserStatusType";
import IUserResponseItem from "interfaces/responses/IUserResponseItem";
import { UserQuickFilterDisplay } from "interfaces/models/UserQuickFilterDisplay";
import { useNavigate } from "react-router-dom";
import SearchEntry from "components/SearchEntry/SearchEntry";
import { FunnelFill } from "react-bootstrap-icons";
import UserFilterDrawer from "domains/Home/Users/UserFilterDrawer/UserFilterDrawer";
import { useStateMachine } from "little-state-machine";
import { updateIsUserFilterDrawerOpen } from "actions/updateIsUserFilterDrawerOpen";
import { updateUserFilter } from "actions/updateUserFilter";
import { updateGroupsFiltered } from "actions/updateGroupsFilteredUsers";
import { updateUserTypes } from "actions/updateUserTypes";
import AmplifyError from "components/AmplifyError/AmplifyError";
import UserItem from "./UserItem/UserItem";

import "./Users.scss";

const Users = () => {
  const { state } = useStateMachine();

  const {
    isSending: getUserIsSending, errors: getUserErrors, data: getUserData, sendRequest: getUserSendRequest
  } = useAmplifyREST();

  const {
    isSending: getGroupsIsSending, errors: getGroupsErrors, data: getGroupsData, sendRequest: getGroupsSendRequest
  } = useAmplifyREST();

  const isSending = getUserIsSending || getGroupsIsSending;
  const errors = getUserErrors || getGroupsErrors;

  const { actions } = useStateMachine({
    updateIsUserFilterDrawerOpen,
    updateUserFilter,
    updateGroupsFiltered,
    updateUserTypes
  });

  const [users, setUsers] = useState<IUser[] | undefined>(undefined);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState<IUser[] | undefined>(undefined);
  const navigate = useNavigate();

  const renderUsers = () => {
    if (filteredUsers) {
      return filteredUsers.map((user: IUser) => (
        <UserItem key={user.userId.toString()} user={user} />));
    }

    return (<></>);
  };

  const filterUsers = () => {
    if (users) {
      let usersFiltered = users;

      if (state.userFilter!.quickFilter !== UserQuickFilter.All) {
        if (state.userFilter!.quickFilter === UserQuickFilter.Active) {
          usersFiltered = usersFiltered.filter(user => user.status === UserStatusType.Active);
        } else if (state.userFilter!.quickFilter === UserQuickFilter.Inactive) {
          usersFiltered = usersFiltered.filter(user => user.status === UserStatusType.Inactive);
        } else if (state.userFilter!.quickFilter === UserQuickFilter.RequiresAction) {
          usersFiltered = usersFiltered.filter(user => user.requests! === true);
        }
      }

      if (state.userFilter!.searchText) {
        const searchTerm = new RegExp(state.userFilter!.searchText, "gi");
        usersFiltered = usersFiltered.filter(user => user.emailAddress.match(searchTerm) || user.username.match(searchTerm));
      }

      if (![0, 3].includes(state.userFilter.userTypes.length)) {
        usersFiltered = usersFiltered.filter(user => (
          (
            (state.userFilter.userTypes.includes(UserType.Local) && !user.isFederatedUser)
            || (state.userFilter.userTypes.includes(UserType.Federated) && user.isFederatedUser)
            || (state.userFilter.userTypes.includes(UserType.SSOPortalAdmin) && user.isPortalAdmin))
        ));
      }

      if (state.userFilter.groups.length > 0) {
        const userFilterGroupIds = state.userFilter!.groups;

        usersFiltered = usersFiltered.filter(user => {
          const userGroupIds = user.groups!.map(group => group.groupId);

          if (userFilterGroupIds.some(userFilterGroupId => userGroupIds.includes(userFilterGroupId))) {
            return user;
          }

          return undefined;
        });
      }

      setFilteredUsers(usersFiltered);

      const groupsFiltered = usersFiltered
        .flatMap(user => user.groups!)
        .filter(
          (thing, i, arr) => arr.findIndex(t => t.groupId === thing.groupId) === i
        )
        .sort((a, b) => a.groupName.localeCompare(b.groupName));

      actions.updateGroupsFiltered(groupsFiltered);

      const userTypesFiltered: UserType[] = [];

      if (usersFiltered.some(user => user.isFederatedUser)) {
        userTypesFiltered.push(UserType.Federated);
      }
      if (usersFiltered.some(user => !user.isFederatedUser)) {
        userTypesFiltered.push(UserType.Local);
      }

      if (usersFiltered.some(user => user.isPortalAdmin)) {
        userTypesFiltered.push(UserType.SSOPortalAdmin);
      }

      actions.updateUserTypes(userTypesFiltered);
    }
  };

  useEffect(() => {
    if (state.userFilter) {
      filterUsers();
    }
  }, [state.userFilter]);

  const onCloseMenu = useCallback(() => {
    actions.updateIsUserFilterDrawerOpen(false);
  }, [actions]);

  const handleQuickFilterChange = (e: UserQuickFilter) => {
    if (state.userFilter) {
      const userFilter = { ...state.userFilter };
      userFilter!.quickFilter = e;
      actions.updateUserFilter(userFilter);
    }
  };

  const handleTextSearch = (searchText: string) => {
    if (state.userFilter) {
      const userFilter = { ...state.userFilter };
      userFilter!.searchText = searchText;
      actions.updateUserFilter(userFilter);
    }
  };

  useEffect(() => {
    getUserSendRequest("users", "GET", null);
  }, [getUserSendRequest]);

  useEffect(() => {
    if (getUserData && getGroupsData) {
      const userList = getUserData.map((userResponse : IUserResponseItem) => mapUserResponseItemToUser(userResponse, getGroupsData)) as IUser[];
      setUsers(userList);
      setFilteredUsers(userList);
    }
  }, [getUserData, getGroupsData]);

  useEffect(() => {
    getGroupsSendRequest("groups", "GET");
  }, [getGroupsSendRequest]);

  useEffect(() => {
    if (!isFilterApplied && users) {
      filterUsers();
      setIsFilterApplied(true);
    }
  }, [isFilterApplied, users]);

  return (
    <>
      <UserFilterDrawer isOpen={state.isUserFilterDrawerOpen} onClose={onCloseMenu} />
      {errors && <AmplifyError errors={errors} />}
      <Card>
        <Card.Header>
          <Row className="w-100">
            <Col xs={8}>
              <h1>
                User Management
                <Spinner animation="grow" visible={isSending} spinnerText="Loading" variant="info" />
              </h1>
            </Col>
            <Col xs={4} className="d-flex justify-content-end">
              <IconButton
                data-testid="btnCreateUser"
                iconType={IconButtonType.Add}
                className="btn-create-user"
                variant="outline-primary"
                onClick={() => navigate("/createuser")}
              >
                New User
              </IconButton>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body className="no-padding">
          <Row className="w-100 align-items-center top-margin-small bottom-margin-small">
            <Col xs={12} md={7} className="d-flex">
              <ToggleButtonGroup
                type="radio"
                name="filter"
                className="quick-filter-wrapper"
                onChange={handleQuickFilterChange}
                defaultValue={state.userFilter.quickFilter}
              >
                <ToggleButton
                  id="users_all"
                  data-testid="users_all"
                  variant="outline-primary"
                  value={UserQuickFilter.All}
                >
                  {UserQuickFilterDisplay[UserQuickFilter.All]}
                </ToggleButton>
                <ToggleButton
                  id="users_active"
                  data-testid="users_active"
                  variant="outline-primary"
                  value={UserQuickFilter.Active}
                >
                  Active
                </ToggleButton>
                <ToggleButton
                  id="users_requires_action"
                  data-testid="users_requires_action"
                  variant="outline-primary"
                  value={UserQuickFilter.RequiresAction}
                >
                  Requires Action
                </ToggleButton>
                <ToggleButton
                  id="users_inactive"
                  data-testid="users_inactive"
                  variant="outline-primary"
                  value={UserQuickFilter.Inactive}
                >
                  Inactive

                </ToggleButton>
              </ToggleButtonGroup>
            </Col>
            <Col xs={12} md={5} className="d-flex justify-content-end">
              <div className="d-flex user-filter">
                <SearchEntry defaultValue={state.userFilter!.searchText!} onSearch={handleTextSearch} />
                <button
                  data-testid="btnUserFilter"
                  className="btn btn-filter"
                  name="filterButton"
                  type="button"
                  aria-label="Open User Filter"
                  onClick={() => { actions.updateIsUserFilterDrawerOpen(true); }}
                >
                  <FunnelFill size={30} data-testid="filterButton" />
                </button>
              </div>
            </Col>
          </Row>

          <Divider />
          <Row className="list-heading">
            <Col xs={6} lg={2}>
              <Row>
                <Col xs={2} />
                <Col xs={10}>
                  User Name
                </Col>

              </Row>
            </Col>
            <Col lg={4} className="right-align d-none d-lg-block" data-testid="emailHeader">Email Address</Col>
            <Col lg={2} className="right-align d-none d-xl-flex justify-content-center" data-testid="groupsSitesHeader">Groups/Sites</Col>
            <Col lg={2} className="right-align d-none d-xl-flex justify-content-center" data-testid="statusHeader">Status</Col>
            <Col xs={6} lg={2} />
          </Row>
          <Divider className="no-display-sm" />
          {filteredUsers && (
            <>
              <NoData visible={filteredUsers.length === 0} description="No Users Found" />

              <ListGroup data-testid="user-list" variant="flush" className="striped highlight">
                {renderUsers()}
              </ListGroup>
            </>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default Users;
